import type {
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { KambiBettingParams } from '@core/Areas/KambiSports/helpers/api/betting/types'
import { formatDollarDecimalToThousandMultplier as convertToKambiSystemCurrency } from '@core/Areas/KambiSports/helpers/currencyFormat'

export function createBetRequest({
  selection,
  investmentWin,
}: CreateBetRequestParams): Pick<KambiBettingParams, 'bets' | 'couponRows'> {
  if (isKambiSportsSingleSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSingleCouponRow(selection)],
    }
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSameGameMultiCouponRow(selection)],
    }
  }

  if (isKambiSportsMultiSelection(selection)) {
    const { legs } = selection

    const couponRows: CouponRows = legs.map((leg, index) =>
      isKambiSportsSingleSelection(leg)
        ? buildSingleCouponRow(leg, index)
        : buildSameGameMultiCouponRow(leg, index)
    )

    return {
      bets: [
        {
          couponRowIndexes: couponRows.map((_, index) => index),
          stake: convertToKambiSystemCurrency(investmentWin),
        },
      ],
      couponRows,
    }
  }

  return { couponRows: [], bets: [] }
}

// =============
// Local Helpers
// =============

function buildSingleCouponRow(selection: KambiSportsSingleSelection, index = 0): CouponRows[0] {
  const { odds, id } = selection.betOffer.outcomes[0]
  return { index, odds, outcomeId: id, type: 'SIMPLE' }
}

function buildSameGameMultiCouponRow(
  selection: KambiSportsSameGameMultiSelection,
  index = 0
): CouponRows[0] {
  const { betOffers, odds } = selection
  return {
    index,
    odds,
    type: 'BET_BUILDER',
    group: {
      operation: 'AND',
      groups: betOffers.map(({ outcomes }) => ({
        operation: 'AND',
        outcomeIds: [outcomes[0].id],
      })),
    },
  }
}

// =====
// Types
// =====

type CreateBetRequestParams = {
  selection: KambiSportsSelection
  investmentWin: number
}

type CouponRows = KambiBettingParams['couponRows']
